import React, { useState, useRef, useEffect } from 'react';
import { Send, Menu, Camera, Mic } from 'lucide-react';

const baseUrl = "https://farmerchat.farmstack.co/opensource-be";
const API_URL = `${baseUrl}/api/chat/get_answer_for_text_query/`;
const LANGUAGES_API_URL = `${baseUrl}/api/language/languages/?email_id=support@farmergpt.chat`;
const OPENAI_API_URL = "https://api.openai.com/v1/chat/completions";
const OPENAI_API_KEY = "sk-4U59-WygNzHEu5Cez_R8nJiDduh_NyhL3SyOE_G8i3T3BlbkFJb2hOQGMumXKm65A7Bjo4n-VGsNb6I7ZCIu6saFqHAA"; // Replace with your actual OpenAI API key

const FarmerChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [showWelcome, setShowWelcome] = useState(true);
  const [languages, setLanguages] = useState(["English", "español", "हिंदी"]);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [loading, setLoading] = useState(false); // Add loading state
  const [context, setContext] = useState(null); // Add context state
  const messagesEndRef = useRef(null); // Define useRef at the top level

  const popularQuestions = [
    "What crops are best for this season?",
    "How to protect crops from pests?",
    "Best irrigation practices?",
    "When should I harvest my crops?"
  ];

  const processApiResponse = (data) => {
    if (!data.response) {
      throw new Error(data.message || 'No answer returned from API');
    }

    setMessages(prev => [...prev, { text: data.response, sender: 'bot' }]);

    if (data.follow_up_questions && data.follow_up_questions.length > 0) {
      setFollowUpQuestions(data.follow_up_questions);
    } else {
      setFollowUpQuestions([]);
    }

    // Update context based on the response
    setContext(data.context || null);
  };

  const handleSend = async () => {
    if (inputMessage.trim()) {
      setMessages([...messages, { text: inputMessage, sender: 'user' }]);
      setInputMessage('');
      setShowWelcome(false);
      setLoading(true); // Set loading to true
  
      // Check for variations of the question "Who made you?"
      const lowerCaseInput = inputMessage.toLowerCase();
      if (lowerCaseInput === "who made you?" || lowerCaseInput === "who developed you?" || lowerCaseInput === "who created you?") {
        setMessages(prev => [...prev, { text: "I was made by a team of developers at MuhuAI.", sender: 'bot' }]);
        setLoading(false); // Set loading to false
        return;
      }
  
      const data = { query: inputMessage, email_id: 'support@farmergpt.chat', context };
  
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
  
        if (!response.ok) {
          if (response.status === 401) {
            const errorData = await response.json();
            console.error('Unauthorized:', errorData.message);
            throw new Error("Unauthorized");
          }
          throw new Error("Error fetching answer from primary server");
        }
  
        const responseData = await response.json();
        console.log("Success:", responseData);
        processApiResponse(responseData);
      } catch (error) {
        console.error("Primary server error:", error);
        // Fallback to OpenAI API
        try {
          const openAiData = {
            model: "gpt-3.5-turbo",
            messages: [{ role: "user", content: inputMessage }],
            temperature: 0.7,
          };
  
          const openAiResponse = await fetch(OPENAI_API_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${OPENAI_API_KEY}`,
            },
            body: JSON.stringify(openAiData),
          });
  
          if (!openAiResponse.ok) {
            const errorData = await openAiResponse.json();
            console.error("OpenAI API Error:", errorData);
            throw new Error("Error fetching answer from OpenAI. Please try again.");
          }
  
          const openAiResult = await openAiResponse.json();
          console.log("OpenAI Response:", openAiResult);
          processApiResponse({ response: openAiResult.choices[0].message.content, context: null });
        } catch (fetchError) {
          console.error("Fetch Error:", fetchError);
          setMessages(prev => [...prev, { text: 'Error fetching answer. Please try again.', sender: 'bot' }]);
        }
      } finally {
        setLoading(false); // Set loading to false
      }
    }
  };

  const handlePopularQuestion = (question) => {
    setInputMessage(question);
    setShowWelcome(false);
    handleSend();
  };

  const handleFollowUpQuestion = (question) => {
    setInputMessage(question);
    setShowWelcome(false);
    handleSend();
  };

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const fetchSupportedLanguages = async () => {
      try {
        const response = await fetch(LANGUAGES_API_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.error('Unauthorized');
            throw new Error('Unauthorized');
          }
          const errorData = await response.json();
          console.error('Error fetching languages:', response.status, errorData);
          throw new Error('Network response was not ok');
        }

        const data = await response.json();

        if (!data.error) {
          setLanguages(data.language_data.map(lang => lang.language_display_name));
          localStorage.setItem('language', data.language_data[0].language_bcp_code);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    fetchSupportedLanguages();
  }, []);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed md:static w-64 bg-white border-r px-4 py-8 h-full z-30 transition-transform duration-300 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="flex flex-col items-center mt-4 -mx-4">
          <div className="w-60 h-12 mb-4">
            <img 
              src="https://muhuconsult.com/wp-content/uploads/2024/10/farmergpt-homepage-Logo.png"
              alt="FarmerGPT Logo"
              className="w-full h-full object-contain"
            />
          </div> 
          <h2 className="mx-auto font-bold text-xl text-gray-800 mb-8">Farmergpt.Chat</h2>
          <div className="w-full px-4">
            {languages.map(lang => (
              <button
                key={lang}
                onClick={() => setSelectedLanguage(lang)}
                className={`w-full text-left py-2 px-4 rounded-lg ${selectedLanguage === lang ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'}`}
              >
                {lang}
              </button>
            ))}
          </div>
           {/* Footer */}

           <div className="fixed bottom-8 mt-8 pt-4">
              <p className="text-sm text-gray-500">
                Developed by <a href="https://nimarungi.org" className="text-gray-500 hover:text-gray-700">NIRUDI</a>
              </p>
              <div className="flex justify-center space-x-4 mt-2">
              <a href="https://nirudi.org/terms/" className="text-gray-500 hover:text-gray-700">
                  Terms
                </a>
                <a href="https://nirudi.org/privacy/" className="text-gray-500 hover:text-gray-700">
                  Privacy
                </a>
                <a href="https://nirudi.org/contact/" className="text-gray-500 hover:text-gray-700">
                  Contact
                </a>
              </div>
          </div> 
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Header */}
          <header className="bg-white border-b p-4 flex justify-between items-center">
            <button 
              className="md:hidden"
              onClick={() => setSidebarOpen(!isSidebarOpen)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-4">
              {/* Remove the green dot */}
              {/* <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" /> */}
              <h1 className="font-semibold text-xl text-gray-800">Farmergpt.Chat</h1>
            </div>
            <div className="flex space-x-2">
              <button className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Sign In</button>
              <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 hidden md:block">Sign Up</button> {/* Hide on small screens */}
            </div>
          </header>

          {/* Chat Container */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              <div 
                ref={messagesEndRef} // Use the ref here
                className={`flex-1 p-4 overflow-y-auto pb-24 ${showWelcome ? 'flex items-center justify-center' : ''}`} // Conditionally apply Flexbox utilities
              >
                {showWelcome ? (
                  <div className="welcome-screen flex flex-col items-center justify-center p-8 text-center">
                    <div className="w-60 h-20 mb-6">
                      <img 
                        src="https://muhuconsult.com/wp-content/uploads/2024/10/farmergpt-homepage-Logo.png"
                        alt="FarmerGPT Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to Farmergpt.Chat</h2>
                    <p className="mb-8 text-gray-600">
                      Your personal farming assistant, ready to help with agricultural queries,
                      crop management, and farming best practices. Ask me anything!
                    </p>
                    <div className="w-full max-w-2xl hidden md:block"> {/* Hide on small screens */}
                      <h3 className="font-semibold text-lg mb-4">Popular Questions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {popularQuestions.map((question, index) => (
                          <button
                            key={index}
                            onClick={() => handlePopularQuestion(question)}
                            className="p-4 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            message.sender === 'user'
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {message.text}
                        </div>
                      </div>
                    ))}
                    {loading && ( // Show loading indicator
                      <div className="flex justify-center">
                        <div className="loader">Loading...</div>
                      </div>
                    )}
                  </div>
                )}

                {followUpQuestions.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 font-bold">Follow-up Questions:</div>
                    <ul className="list-disc pl-5 space-y-2">
                      {followUpQuestions.map((questionObj, index) => (
                        <li key={index} className="flex justify-between items-center">
                          <span>{questionObj.question}</span> {/* Render the question text */}
                          <button
                            onClick={() => handleFollowUpQuestion(questionObj.question)}
                            className="ml-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded"
                          >
                            <i className="fa-solid fa-paper-plane"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:left-64"> {/* Removed margin-right */}
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-100 rounded-lg flex items-center p-2">
                    <button className="p-2 hover:bg-gray-200 rounded-full">
                      <Camera className="h-5 w-5 text-gray-500" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded-full">
                      <Mic className="h-5 w-5 text-gray-500" />
                    </button>
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="flex-1 bg-transparent border-none focus:outline-none px-1 py-1"
                    />
                    <button
                      onClick={handleSend}
                      className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FarmerChat;